import React, { useRef, useState, useEffect } from 'react';

// Drop-in replacement for <input type="date"> — shows 15-Oct-2026 on every computer.
//  • TYPE the date: 15-10-2026, 15/10/26, 15 oct 2026, 15102026 ...  (applied when you leave the box or press Enter)
//  • or click the calendar icon to pick it. Backspace/Delete in the empty box clears the date.
// The value handed to the page is still 'YYYY-MM-DD', exactly like a normal date input.
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const show = v => { const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}-${MON[Number(m[2]) - 1]}-${m[1]}` : ''; };
const p2 = n => String(n).padStart(2, '0');

function fullYear(y) {
  if (y.length === 4) return Number(y);
  const n = Number(y), cur = new Date().getFullYear() % 100;
  return n > cur + 10 ? 1900 + n : 2000 + n;   // 90 -> 1990, 26 -> 2026
}
// typed text -> 'YYYY-MM-DD' | '' (empty) | null (not understood)
function parseTyped(text) {
  const t = String(text || '').trim();
  if (!t) return '';
  let d, m, y, r;
  if ((r = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) { y = Number(r[1]); m = Number(r[2]); d = Number(r[3]); }
  else if ((r = t.match(/^(\d{1,2})[-/. ](\d{1,2})[-/. ](\d{4}|\d{2})$/))) { d = Number(r[1]); m = Number(r[2]); y = fullYear(r[3]); }
  else if ((r = t.match(/^(\d{1,2})[-/. ]?([A-Za-z]{3,9})[-/. ,]*(\d{4}|\d{2})$/))) {
    d = Number(r[1]); m = MON.findIndex(x => x.toLowerCase() === r[2].slice(0, 3).toLowerCase()) + 1; y = fullYear(r[3]);
    if (!m) return null;
  }
  else if ((r = t.match(/^(\d{2})(\d{2})(\d{4})$/))) { d = Number(r[1]); m = Number(r[2]); y = Number(r[3]); }
  else if ((r = t.match(/^(\d{2})(\d{2})(\d{2})$/))) { d = Number(r[1]); m = Number(r[2]); y = fullYear(r[3]); }
  else return null;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return `${y}-${p2(m)}-${p2(d)}`;
}

if (typeof document !== 'undefined' && !document.getElementById('dmy-date-css')) {
  const st = document.createElement('style');
  st.id = 'dmy-date-css';
  st.textContent = `
    .dmy-wrap{position:relative;display:inline-block;max-width:100%}
    .form-group > .dmy-wrap,.dmy-wrap.dmy-block{display:block;width:100%}
    .dmy-wrap > input.dmy-input{width:100%;box-sizing:border-box;padding-right:28px}
    .dmy-wrap > input.dmy-bad{border-color:#dc2626 !important}
    .dmy-wrap > .dmy-cal{position:absolute;right:4px;top:50%;transform:translateY(-50%);border:none;background:none;padding:2px;cursor:pointer;line-height:0;color:var(--text3)}
    .dmy-wrap > .dmy-cal:hover{color:var(--accent)}
    .dmy-wrap > .dmy-cal:disabled{cursor:not-allowed;opacity:.5}
    .dmy-wrap > .dmy-native{position:absolute;left:0;bottom:0;width:100%;height:1px;opacity:0;pointer-events:none;border:0;padding:0}
  `;
  document.head.appendChild(st);
}

const WRAP_KEYS = ['width', 'minWidth', 'maxWidth', 'flex', 'flexShrink', 'flexGrow', 'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft', 'alignSelf', 'gridColumn', 'display'];

export default function DateInput({ value, onChange, style, className, placeholder, disabled, readOnly, min, max, onBlur, onFocus, autoFocus, title, ...rest }) {
  const nativeRef = useRef(null);
  const [draft, setDraft] = useState(show(value));
  const [bad, setBad] = useState(false);
  const editing = useRef(false);

  // follow the value from the page whenever the user is not typing
  useEffect(() => { if (!editing.current) { setDraft(show(value)); setBad(false); } }, [value]);

  const s = style || {};
  const wrapStyle = {}, inputStyle = {};
  Object.keys(s).forEach(k => { (WRAP_KEYS.includes(k) ? wrapStyle : inputStyle)[k] = s[k]; });

  function push(iso) {   // tell the page about the new value (same event shape as a normal date input)
    const el = nativeRef.current;
    if (!el || (el.value || '') === iso) return;
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    setter.call(el, iso);
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }
  function commit() {
    editing.current = false;
    const iso = parseTyped(draft);
    if (iso === null || (iso && ((min && iso < min) || (max && iso > max)))) {   // not a real date / outside min-max: go back
      setDraft(show(value)); setBad(false); return;
    }
    setBad(false);
    if (iso !== (value || '')) push(iso);
    else setDraft(show(value));
    if (iso) setDraft(show(iso));
  }
  const openPicker = () => { if (disabled || readOnly) return; try { nativeRef.current && nativeRef.current.showPicker && nativeRef.current.showPicker(); } catch (err) { /* already open */ } };

  return (
    <span className="dmy-wrap" style={wrapStyle}>
      <input type="text" inputMode="numeric" autoComplete="off" spellCheck={false} autoFocus={autoFocus} title={title}
        className={`dmy-input${bad ? ' dmy-bad' : ''}${className ? ' ' + className : ''}`}
        value={draft} placeholder={placeholder || 'dd-mmm-yyyy'} disabled={disabled} readOnly={readOnly} style={inputStyle}
        onFocus={e => { editing.current = true; e.target.select(); if (onFocus) onFocus(e); }}
        onChange={e => { editing.current = true; setDraft(e.target.value); setBad(parseTyped(e.target.value) === null); }}
        onBlur={e => { commit(); if (onBlur) onBlur(e); }}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          else if (e.key === 'Escape') { editing.current = false; setDraft(show(value)); setBad(false); }
          else if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); openPicker(); }
        }} />
      <button type="button" className="dmy-cal" tabIndex={-1} disabled={disabled || readOnly} onClick={openPicker} title="Pick from calendar" aria-label="Pick date">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
      </button>
      <input ref={nativeRef} type="date" className="dmy-native" tabIndex={-1} aria-hidden="true" value={value || ''} onChange={onChange} min={min} max={max} disabled={disabled} {...rest} />
    </span>
  );
}
